import React, { useEffect, useRef, useState } from 'react';
import { manifesto as m, lifecycle } from '../content';
import { GapSection } from '../story';
import { Arrow, Badge, Button, SmartLink, Swipe, cx, prefersReducedMotion, usePageMeta } from '../ui';

/* /why: the manifesto. Told like a short film in scroll.
   Motion follows the brand film rules: cut, don't fade; one word or line at a time;
   the swipe draws left to right on the payoff; photos drift slowly; fade only on the final exit. */

// Each generated photo has a safety net: an approved library image, or none (the chapter drops to type).
const FALLBACK: Record<string, string | null> = {
  '/images/v2/gen/why-instructions.jpg': '/images/v2/unboxing.jpg',
  // No stand-in where a library photo would contradict the line (a treadmill repair is not "one bolt short",
  // a working amplifier is not "it does not"). Those chapters drop to type until their own photo lands.
  '/images/v2/gen/why-missing-part.jpg': null,
  '/images/v2/gen/why-drawer.jpg': null,
  '/images/v2/gen/why-broken.jpg': null,
  '/images/v2/gen/why-repair.jpg': '/images/v2/service.jpg',
  '/images/v2/gen/why-landfill.jpg': null, // no stand-in: the caption carries it alone
};

const ALT: Record<string, string> = {
  '/images/v2/gen/why-instructions.jpg': 'Someone on a living room floor among the parts of a new appliance, frowning at a folded instruction sheet',
  '/images/v2/gen/why-missing-part.jpg': 'A woman kneels by a half-built barbecue, looking into an empty bag of fittings',
  '/images/v2/gen/why-drawer.jpg': 'A kitchen drawer stuffed with unopened manuals, warranty cards and tangled cables',
  '/images/v2/gen/why-broken.jpg': 'A man in pyjamas and a coat crouches by a heat pump on a frosty morning, on hold on his phone',
  '/images/v2/gen/why-repair.jpg': 'A woman at her kitchen table fits a new brush roll to an old upright vacuum cleaner',
  '/images/v2/gen/why-landfill.jpg': 'A recycling centre skip full of discarded kettles, a microwave and a vacuum cleaner',
};
const FALLBACK_ALT: Record<string, string> = {
  '/images/v2/unboxing.jpg': 'A woman lifts a new coffee machine out of its box on a kitchen counter',
  '/images/v2/gen/fitness-service-b.jpg': 'A technician kneeling beside an opened treadmill with parts laid out on the floor',
  '/images/v2/gen/heatpump-home.jpg': 'A hand turning a heating control on a quiet hallway wall',
  '/images/v2/gen/hifi-owner.jpg': 'A hand on the volume knob of an amplifier at home',
  '/images/v2/service.jpg': 'A technician working on an espresso machine in a small kitchen',
};

// Where the subject sits, so crops keep it.
const POS: Record<string, string> = {
  '/images/v2/gen/why-instructions.jpg': '64% 32%',
  '/images/v2/gen/why-missing-part.jpg': '62% 50%',
  '/images/v2/gen/why-drawer.jpg': '58% 50%',
  '/images/v2/gen/why-broken.jpg': '45% 50%',
  '/images/v2/gen/why-repair.jpg': '52% 40%',
  '/images/v2/gen/why-landfill.jpg': '60% 55%',
  '/images/v2/service.jpg': '78% 50%',
  '/images/v2/unboxing.jpg': '64% 50%',
  '/images/v2/gen/heatpump-home.jpg': '62% 50%',
};

const TIME = ['Day 1', 'Day 2', 'Month 3', 'Year 2'];
const SWIPES = ['get it working.', 'missing.', 'the manual.', 'it does not.'];

const sentences = (s: string) => s.split(/(?<=\.)\s+/);

// Wrap one short phrase of a line in the swipe.
function withSwipe(text: string, phrase: string) {
  const i = text.lastIndexOf(phrase);
  if (i < 0) return text;
  return (
    <>
      {text.slice(0, i)}
      <Swipe>{phrase}</Swipe>
      {text.slice(i + phrase.length)}
    </>
  );
}

// Adds .is-on once the scene has scrolled well into view. Reduced motion: on from the start.
function useOn<T extends HTMLElement>(margin = '-28%') {
  const ref = useRef<T>(null);
  const [on, setOn] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
      setOn(true);
      return;
    }
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setOn(true);
          io.disconnect();
        }
      },
      { rootMargin: `0px 0px ${margin} 0px` }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [margin]);
  return [ref, on] as const;
}

function Photo({ src, className, eager, onMissing, sizes }: { src: string; className?: string; eager?: boolean; onMissing?: () => void; sizes?: string }) {
  const [cur, setCur] = useState(src);
  const [gone, setGone] = useState(false);
  if (gone) return null;
  return (
    <img
      src={cur}
      alt={ALT[cur] ?? FALLBACK_ALT[cur] ?? ''}
      width={1536}
      height={1024}
      sizes={sizes}
      loading={eager ? 'eager' : 'lazy'}
      decoding="async"
      className={className}
      style={POS[cur] ? { objectPosition: POS[cur] } : undefined}
      onError={() => {
        const fb = FALLBACK[src];
        if (cur === src && fb) setCur(fb);
        else {
          setGone(true);
          onMissing?.();
        }
      }}
    />
  );
}

// The ownership timeline: the same four marks in every chapter, the current one lit.
function Ruler({ at, dark }: { at: number; dark?: boolean }) {
  return (
    <ol className={cx('why-ruler', dark && 'is-dark')} aria-label="Time since purchase">
      {TIME.map((t, i) => (
        <li key={t} className={cx(i === at && 'is-at', i < at && 'is-past')} aria-current={i === at ? 'step' : undefined}>
          <span className="why-ruler-dot" />
          <span className="why-ruler-t">{t}</span>
        </li>
      ))}
    </ol>
  );
}

function Opening() {
  const [go, setGo] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setGo(true), 120);
    return () => window.clearTimeout(t);
  }, []);
  const words = ['We', 'have', 'all'];
  return (
    <header className={cx('why-open', go && 'is-on')}>
      <div className="wrap why-open-in">
        <h1 className="why-h1">
          {words.map((w, i) => (
            <React.Fragment key={w}>
              <span className="why-w" style={{ '--d': `${0.15 + i * 0.35}s` } as React.CSSProperties}>
                {w}
              </span>{' '}
            </React.Fragment>
          ))}
          <br />
          <span className="why-w" style={{ '--d': '1.2s' } as React.CSSProperties}>
            <Swipe>been there.</Swipe>
          </span>
        </h1>
        <div className="why-open-foot">
          <p className="why-open-line">{m.open}</p>
          <Ruler at={-1} />
        </div>
      </div>
    </header>
  );
}

type Moment = (typeof m.moments)[number];

function Chapter({ mo, i }: { mo: Moment; i: number }) {
  const [ref, on] = useOn<HTMLElement>();
  const [bare, setBare] = useState(false);
  const bleed = i % 2 === 0;
  const head = (
    <>
      <Ruler at={i} dark={bleed} />
      <h2 className="why-ch-t">{withSwipe(mo.t, SWIPES[i])}</h2>
      <p className="why-ch-d">{mo.d}</p>
    </>
  );
  if (bleed) {
    return (
      <section ref={ref} className={cx('why-ch why-bleed on-dark', bare && 'is-bare', on && 'is-on')} aria-label={`${TIME[i]}: ${mo.k}`}>
        {!bare && (
          <div className="why-bleed-img">
            <Photo src={mo.img} className="why-drift" eager={i === 0} sizes="100vw" onMissing={() => setBare(true)} />
          </div>
        )}
        <div className="why-bleed-scrim" aria-hidden="true" />
        <div className="wrap why-bleed-in">
          <div className="why-ch-copy">{head}</div>
        </div>
      </section>
    );
  }
  return (
    <section ref={ref} className={cx('why-ch why-framed', i === 3 && 'is-flip', bare && 'is-bare', on && 'is-on')} aria-label={`${TIME[i]}: ${mo.k}`}>
      <div className="wrap">
        <div className="why-framed-grid">
          <div className="why-ch-copy">{head}</div>
          {!bare && (
            <div className="frame why-framed-ph">
              <div>
                <Photo src={mo.img} className="why-drift" sizes="(min-width: 900px) 60vw, 100vw" onMissing={() => setBare(true)} />
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function Turn() {
  const [ref, on] = useOn<HTMLElement>('-22%');
  const s = sentences(m.turn.body);
  const first = s[0];
  const costs = s.slice(1, -1);
  const last = s[s.length - 1];
  return (
    <section ref={ref} className={cx('why-turn on-dark', on && 'is-on')}>
      <div className="wrap why-turn-grid">
        <h2 className="why-turn-t">
          <span className="why-turn-a">{m.turn.title[0]}</span>{' '}
          <span className="why-turn-b">{withSwipe(m.turn.title[1], 'the maker')}</span>
        </h2>
        <div className="why-turn-body">
          <p className="why-turn-first">{first}</p>
          <ul className="why-costs">
            {costs.map((c, i) => (
              <li key={c} style={{ '--d': `${0.3 + i * 0.35}s` } as React.CSSProperties}>
                {c}
              </li>
            ))}
          </ul>
          <p className="why-turn-last" style={{ '--d': `${0.3 + costs.length * 0.35 + 0.2}s` } as React.CSSProperties}>
            {last}
          </p>
        </div>
      </div>
    </section>
  );
}

function Belief() {
  const [ref, on] = useOn<HTMLElement>('-30%');
  return (
    <section ref={ref} className={cx('why-belief', on && 'is-on')}>
      <div className="wrap">
        <h2 className="why-belief-t">
          <span className="why-belief-a">{m.belief.title[0]}</span>{' '}
          <span className="why-belief-b">
            <Swipe>{m.belief.title[1]}</Swipe>
          </span>
        </h2>
        <div className="why-belief-grid">
          <ol className="why-belief-lines">
            {m.belief.lines.map((l, i) => (
              <li key={l} style={{ '--d': `${0.9 + i * 0.4}s` } as React.CSSProperties}>
                {l}
              </li>
            ))}
          </ol>
          <figure className="frame why-belief-ph">
            <div>
              <Photo src={m.belief.img} className="why-drift" sizes="(min-width: 900px) 40vw, 100vw" />
            </div>
          </figure>
        </div>
      </div>
    </section>
  );
}

function Disposable() {
  const [ref, on] = useOn<HTMLElement>('-20%');
  const [missing, setMissing] = useState(false);
  // Local wording: no unsourced "most", and no "here" when there is no photo to point at.
  const [a, ...rest] = sentences(missing ? 'Too much of what ends up in landfill was never worn out. It was abandoned.' : 'Too much of what ends up here was never worn out. It was abandoned.');
  return (
    <section ref={ref} className={cx('why-disp', missing && 'is-bare', on && 'is-on')}>
      {!missing && (
        <div className="why-disp-img">
          <Photo src={m.disposable.img} className="why-drift" onMissing={() => setMissing(true)} sizes="100vw" />
        </div>
      )}
      <div className="wrap">
        <p className="why-disp-cap">
          <span>{a}</span> <strong>{rest.join(' ')}</strong>
        </p>
      </div>
    </section>
  );
}

function Answer() {
  const [ref, on] = useOn<HTMLElement>('-25%');
  return (
    <section ref={ref} className={cx('why-answer', on && 'is-on')}>
      <div className="wrap">
        <div className="why-answer-head">
          <h2 className="why-answer-t">
            {m.answer.title[0]}
            <br />
            {withSwipe(m.answer.title[1], 'keep in touch.')}
          </h2>
          <p className="why-answer-body">{m.answer.body}</p>
        </div>

        <div className="why-life">
          <p className="why-life-k">{lifecycle.eyebrow}</p>
          <ol className="why-life-row">
            {lifecycle.stages.map((s) => (
              <li key={s.k}>
                <span className="why-life-dot" aria-hidden="true" />
                <b>{s.k}</b>
                <span>{s.t}</span>
              </li>
            ))}
          </ol>
        </div>

        <p className="why-links">
          <SmartLink href="/platform" className="why-link">
            See the platform <Arrow />
          </SmartLink>
        </p>
      </div>
    </section>
  );
}

function Makers() {
  const [ref, on] = useOn<HTMLElement>('-25%');
  return (
    <section ref={ref} className={cx('why-makers', on && 'is-on')}>
      <div className="wrap why-makers-grid">
        <h2 className="why-makers-t">{withSwipe(m.makers.title[0], 'make things well.')}</h2>
        <p className="why-makers-body">{m.makers.body}</p>
      </div>
    </section>
  );
}

function Close() {
  const [ref, on] = useOn<HTMLElement>('-20%');
  const [a, b] = m.close.replace(/\.$/, '').split(/ (?=better$)/);
  return (
    <section ref={ref} className={cx('why-close on-dark', on && 'is-on')}>
      <div className="wrap">
        <Badge size={52} className="why-close-badge" />
        <p className="why-close-t">
          {a.replace(/ things$/, '')}
          <br />
          things <Swipe>{b}.</Swipe>
        </p>
        <div className="why-close-cta">
          <Button href="/book-a-demo" kind="go">
            Book 30 minutes <Arrow />
          </Button>
          <SmartLink href="/tour" className="why-close-tour">
            or take the tour
          </SmartLink>
        </div>
      </div>
    </section>
  );
}

export default function WhyPage() {
  usePageMeta(
    'Why we exist',
    'We have all been there. You buy something good, the box arrives, and you are on your own. Things should not be disposable. So we gave every product a way to keep in touch.'
  );
  return (
    <article className="why">
      <style>{CSS}</style>
      <Opening />
      {m.moments.map((mo, i) => (
        <Chapter key={mo.k} mo={mo} i={i} />
      ))}
      <Turn />
      <GapSection tone="light" id="why-gap" compact />
      <Belief />
      <Disposable />
      <Answer />
      <Makers />
      <Close />
    </article>
  );
}

const CSS = `
.why{--why-pad:clamp(64px,7.5vw,116px);overflow-x:clip}
.why h1,.why h2{text-wrap:balance}
.why p{text-wrap:pretty}

/* The end card already says it; the footer's sign-off would repeat it straight after. */


/* Motion primitives. Cut in (opacity snaps), arrive with power3.out, never cross-fade. */
@keyframes why-rise{from{transform:translateY(.28em)}to{transform:none}}
@keyframes why-wipe{from{clip-path:inset(0 100% 0 0)}to{clip-path:inset(0 0 0 0)}}
@keyframes why-drift{from{transform:scale(1.07)}to{transform:scale(1)}}
@keyframes why-exit{from{opacity:0}to{opacity:1}}
.why .swipe::after{transform:scaleX(0);transition:transform .55s var(--bm-ease) var(--sd,.55s)}
.why-h1 .swipe::after,.why-belief-b .swipe::after,.why-close-t .swipe::after{bottom:.17em;height:.15em}
.why .is-on .swipe::after,.why .is-on.swipe::after{transform:scaleX(1)}
.why .why-drift{transform:scale(1.07)}
.why .is-on .why-drift{animation:why-drift 2.4s var(--bm-ease) forwards}

/* Opening */
.why-open{display:flex;padding:clamp(32px,4vw,56px) 0 clamp(48px,5vw,72px)}
.why-open-in{display:flex;flex-direction:column;gap:clamp(28px,3.5vw,48px);width:100%}
.why-h1{font-weight:800;color:var(--bm-indigo);font-size:clamp(44px,7.2vw,104px);line-height:.95;letter-spacing:-.05em;margin:0}
.why-open .swipe{--sd:1.55s}
.why-w{display:inline-block;opacity:0}
.why-open.is-on .why-w{opacity:1;transition:opacity 0s linear var(--d);animation:why-rise .6s var(--bm-ease) var(--d) both}
.why-open-foot{display:grid;grid-template-columns:1fr;gap:40px;align-items:end}
.why-open-line{font-size:clamp(21px,2vw,28px);line-height:1.45;color:var(--bm-ink);letter-spacing:-.012em;max-width:34ch;margin:0;opacity:0}
.why-open.is-on .why-open-line{opacity:1;transition:opacity 0s linear 2.2s}
.why-open .why-ruler{opacity:0}
.why-open.is-on .why-ruler{opacity:1;transition:opacity 0s linear 2.6s}
@media (min-width:900px){.why-open-foot{grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:64px}.why-open-line{grid-column:2;grid-row:1}.why-open .why-ruler{grid-column:1;grid-row:1}}

/* The ruler */
.why-ruler{list-style:none;margin:0;padding:0;display:grid;grid-template-columns:repeat(4,1fr);position:relative;max-width:520px}
.why-ruler::before{content:'';position:absolute;left:0;right:0;top:4px;height:1px;background:var(--bm-line)}
.why-ruler li{position:relative;padding-top:20px;font-weight:500;font-size:13px;color:var(--bm-muted)}
.why-ruler-dot{position:absolute;left:0;top:0;width:9px;height:9px;border-radius:50%;background:#fff;box-shadow:inset 0 0 0 1.5px #C9C7DE}
.why-ruler li.is-past .why-ruler-dot{background:var(--bm-indigo);box-shadow:none}
.why-ruler li.is-at{color:var(--bm-indigo);font-weight:600}
.why-ruler li.is-at .why-ruler-dot{background:var(--bm-gradient);box-shadow:none;width:11px;height:11px;top:-1px}
.why-ruler.is-dark::before{background:rgba(255,255,255,.22)}
.why-ruler.is-dark li{color:rgba(255,255,255,.55)}
.why-ruler.is-dark .why-ruler-dot{background:transparent;box-shadow:inset 0 0 0 1.5px rgba(255,255,255,.4)}
.why-ruler.is-dark li.is-past .why-ruler-dot{background:#fff}
.why-ruler.is-dark li.is-at{color:var(--bm-lime)}

/* Chapter type */
.why-ch .why-ruler{max-width:400px;margin-bottom:clamp(28px,3.5vw,48px)}
.why-ch-k{margin:0 0 18px;font-weight:600;font-size:16px;color:var(--bm-muted);letter-spacing:-.005em}
.why-ch-t{font-weight:800;color:var(--bm-indigo);font-size:clamp(38px,5.2vw,76px);line-height:1;letter-spacing:-.042em;margin:0}
.why-ch-d{font-size:clamp(18px,1.45vw,21px);line-height:1.55;color:var(--bm-muted);margin:24px 0 0;max-width:34ch}
.why-ch .why-ch-copy>*{opacity:0}
.why-ch.is-on .why-ch-copy>*{opacity:1;transition:opacity 0s linear .05s}
.why-ch.is-on .why-ch-copy>:nth-child(2){transition-delay:.2s;animation:why-rise .6s var(--bm-ease) .2s both}
.why-ch.is-on .why-ch-copy>:nth-child(3){animation:why-wipe .7s var(--bm-ease) 1.25s both}
.why-ch .why-ch-copy>:nth-child(3){opacity:1;clip-path:inset(0 100% 0 0)}
.why-ch .swipe{--sd:.75s}

/* Full-bleed chapters */
.why-bleed{position:relative;background:var(--bm-indigo-deep);color:#fff;overflow:hidden}
.why-bleed-img{position:relative;height:64svh;min-height:380px;overflow:hidden}
.why-bleed-img img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;object-position:70% 50%}
.why-bleed-scrim{position:absolute;left:0;right:0;top:calc(64svh - 200px);height:200px;background:linear-gradient(180deg,rgba(24,19,67,0),var(--bm-indigo-deep));pointer-events:none}
.why-bleed-in{position:relative;padding-top:0;padding-bottom:var(--why-pad);margin-top:-56px}
.why-bleed .why-ch-copy{margin-top:8px}
.why-bleed .why-ch-t{color:#fff}
.why-bleed .why-ch-k{color:rgba(255,255,255,.72)}
.why-bleed .why-ch-d{color:rgba(255,255,255,.78)}
@media (min-width:900px){
  .why-bleed{min-height:max(620px,calc(100svh - 120px));display:flex;align-items:flex-end}
  .why-bleed-img{position:absolute;inset:0;height:auto;min-height:0}
  .why-bleed-scrim{inset:0;top:0;height:auto;background:linear-gradient(90deg,rgba(24,19,67,.92) 0%,rgba(24,19,67,.78) 34%,rgba(24,19,67,.18) 66%,rgba(24,19,67,0) 100%),linear-gradient(0deg,rgba(24,19,67,.55),rgba(24,19,67,0) 45%)}
  .why-bleed-in{margin-top:0;padding-top:var(--why-pad);padding-bottom:clamp(64px,7vw,104px);width:100%}
  .why-bleed .why-ch-copy{margin-top:0;max-width:640px}
}

/* Framed chapters */
.why-framed{padding:var(--why-pad) 0;background:#fff}
.why-framed.is-flip{background:var(--bm-paper-alt)}
.why-framed-grid{display:grid;gap:36px;align-items:center}
.why-framed-ph>div{aspect-ratio:4/3}
/* No photo yet: the chapter becomes type alone, never an empty frame or a wrong scene */
.why-bleed.is-bare{min-height:0!important;display:block!important;background:var(--bm-indigo)}
.why-bleed.is-bare .why-bleed-scrim{display:none}
.why-bleed.is-bare .why-bleed-in{margin-top:0;padding-top:var(--why-pad);padding-bottom:var(--why-pad)}
.why-ch.is-bare .why-ch-copy{max-width:none!important}
.why-ch.is-bare .why-ch-t{max-width:13ch}
.why-framed.is-bare .why-framed-grid{grid-template-columns:1fr!important}
.why-ch.is-bare .why-ch-t{font-size:clamp(44px,7vw,104px)}
.why-ch.is-bare .why-ch-d{max-width:34ch;font-size:clamp(18px,1.6vw,22px)}
.why-framed-ph img{width:100%;height:100%;object-fit:cover}
@media (min-width:900px){
  .why-framed-grid{grid-template-columns:5fr 7fr;gap:clamp(48px,6vw,96px)}
  .why-framed.is-flip .why-framed-grid{grid-template-columns:7fr 5fr}
  .why-framed.is-flip .why-ch-copy{order:2}
  .why-framed.is-flip .why-framed-ph{order:1}
}

/* The turn */
.why-turn{background:var(--bm-indigo);color:#fff;padding:var(--why-pad) 0}
.why-turn-grid{display:grid;gap:56px}
.why-turn-t{font-weight:800;font-size:clamp(38px,5vw,72px);line-height:1.02;letter-spacing:-.042em;margin:0;color:#fff}
.why-turn-b{display:block;margin-top:.35em;color:rgba(255,255,255,.62)}
.why-turn-b .swipe{color:#fff}
.why-turn .swipe{--sd:.7s}
.why-turn-first{font-size:clamp(21px,1.8vw,25px);line-height:1.45;margin:0 0 28px;color:#fff;font-weight:500;letter-spacing:-.01em}
.why-costs{list-style:none;margin:0;padding:0;border-top:1px solid rgba(255,255,255,.16)}
.why-costs li{padding:18px 0;border-bottom:1px solid rgba(255,255,255,.16);font-size:clamp(18px,1.5vw,21px);line-height:1.4;color:rgba(255,255,255,.82);opacity:0}
.why-turn-last{margin:36px 0 0;font-size:clamp(21px,1.8vw,25px);line-height:1.45;font-weight:600;color:var(--bm-lime);letter-spacing:-.012em;opacity:0}
.why-turn.is-on .why-costs li,.why-turn.is-on .why-turn-last{opacity:1;transition:opacity 0s linear var(--d)}
@media (min-width:900px){.why-turn-grid{grid-template-columns:6fr 5fr;gap:clamp(56px,7vw,112px);align-items:start}.why-turn-t{position:sticky;top:120px}}

/* Belief: the peak */
.why-belief{padding:var(--why-pad) 0 clamp(64px,8vw,120px);background:#fff}
.why-belief-t{font-weight:800;color:var(--bm-indigo);margin:0;letter-spacing:-.05em}
.why-belief-a{display:block;font-size:clamp(30px,4.4vw,64px);line-height:1;letter-spacing:-.04em;margin-bottom:.12em}
.why-belief-b{display:block;font-size:clamp(52px,13.6vw,188px);line-height:.95}
.why-belief .swipe{--sd:.35s}
.why-belief .swipe::after{transition-duration:.6s}
.why-belief-grid{display:grid;gap:40px;margin-top:clamp(40px,5vw,72px)}
.why-belief-lines{list-style:none;margin:0;padding:0;counter-reset:b}
.why-belief-lines li{font-size:clamp(22px,2.2vw,32px);line-height:1.3;letter-spacing:-.022em;font-weight:600;color:var(--bm-indigo);padding:0 0 clamp(20px,2vw,28px);margin:0 0 clamp(20px,2vw,28px);border-bottom:1px solid var(--bm-line);opacity:0}
.why-belief-lines li:last-child{border-bottom:0;margin-bottom:0}
.why-belief.is-on .why-belief-lines li{opacity:1;transition:opacity 0s linear var(--d);animation:why-rise .6s var(--bm-ease) var(--d) both}
.why-belief-ph{margin:0}
.why-belief-ph>div{aspect-ratio:4/5}
.why-belief-ph img{width:100%;height:100%;object-fit:cover}
@media (min-width:900px){.why-belief-grid{grid-template-columns:7fr 5fr;gap:clamp(56px,7vw,112px);align-items:start}.why-belief-ph{position:sticky;top:104px}}

/* Disposable: sober, documentary */
.why-disp{background:#fff;padding:0 0 var(--why-pad)}
.why-disp-img{height:clamp(320px,52vw,720px);overflow:hidden;position:relative;background:var(--bm-paper-alt)}
.why-disp-img img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover}
.why-disp-cap{margin:28px 0 0;padding-top:20px;border-top:1px solid var(--bm-line);max-width:760px;font-size:clamp(18px,1.6vw,22px);line-height:1.5;color:var(--bm-muted)}
.why-disp-cap strong{color:var(--bm-indigo);font-weight:700}
.why-disp.is-bare{padding-top:clamp(24px,3vw,40px)}
.why-disp.is-bare .why-disp-cap{font-size:clamp(28px,3.4vw,48px);line-height:1.15;letter-spacing:-.03em;max-width:20ch;border-top:0;padding-top:0;margin-top:0}
.why-disp.is-bare .why-disp-cap span{display:block;color:var(--bm-muted);font-weight:500}
.why-disp.is-bare .why-disp-cap strong{display:block;font-weight:800}

/* The answer */
.why-answer{background:var(--bm-paper-alt);padding:var(--why-pad) 0}
.why-answer-head{display:grid;gap:32px}
.why-answer-t{font-weight:800;color:var(--bm-indigo);font-size:clamp(38px,5.2vw,76px);line-height:1;letter-spacing:-.042em;margin:0}
.why-answer-body{font-size:clamp(18px,1.45vw,21px);line-height:1.6;color:var(--bm-ink);margin:0;max-width:44ch}
@media (min-width:900px){.why-answer-head{grid-template-columns:7fr 5fr;gap:clamp(48px,6vw,96px);align-items:end}}
.why-promise{list-style:none;margin:clamp(40px,5vw,64px) 0 0;padding:0;display:grid;grid-template-columns:1fr;border-top:1px solid var(--bm-line)}
.why-promise li{display:flex;align-items:baseline;gap:16px;padding:22px 0;border-bottom:1px solid var(--bm-line);font-size:clamp(19px,1.6vw,23px);line-height:1.35;font-weight:600;letter-spacing:-.016em;color:var(--bm-indigo);opacity:0}
.why-answer.is-on .why-promise li{opacity:1;transition:opacity 0s linear var(--d)}
.why-promise-mark{flex:none;width:9px;height:9px;border-radius:50%;background:var(--bm-gradient);transform:translateY(-.18em)}
@media (min-width:700px){.why-promise{grid-template-columns:1fr 1fr;column-gap:clamp(40px,5vw,80px)}}
.why-life{margin-top:clamp(48px,6vw,80px)}
.why-life-k{margin:0 0 24px;font-weight:600;font-size:14.5px;color:var(--bm-emerald)}
.why-life-row{list-style:none;margin:0;padding:0;display:grid;grid-template-columns:1fr;position:relative}
.why-life-row li{position:relative;display:grid;grid-template-columns:92px 1fr;gap:12px;padding:12px 0 12px 26px;font-size:15.5px;line-height:1.4;color:var(--bm-muted)}
.why-life-row li b{color:var(--bm-indigo);font-weight:700}
.why-life-st{display:block;grid-column:2;font-style:normal;font-weight:600;font-size:12px;color:#B8862B;margin-top:4px}
.why-life-row::before{content:'';position:absolute;left:4px;top:20px;bottom:20px;width:1.5px;background:var(--bm-gradient)}
.why-life-dot{position:absolute;left:0;top:18px;width:10px;height:10px;border-radius:50%;background:var(--bm-paper-alt);box-shadow:inset 0 0 0 2px var(--bm-indigo)}
@media (min-width:900px){
  .why-life-row{grid-template-columns:repeat(7,1fr)}
  .why-life-row::before{left:0;right:0;top:4px;bottom:auto;width:auto;height:1.5px}
  .why-life-row li{display:block;padding:26px 16px 0 0}
  .why-life-row li b{display:block;font-size:16.5px;margin-bottom:4px}
  .why-life-dot{top:0}
}
.why-links{display:flex;flex-wrap:wrap;gap:12px 36px;margin:clamp(36px,4.5vw,56px) 0 0}
.why-link{display:inline-flex;align-items:center;gap:8px;font-weight:600;font-size:16px;color:var(--bm-indigo);text-decoration:underline;text-decoration-color:var(--bm-line);text-decoration-thickness:1.5px;text-underline-offset:6px;transition:text-decoration-color .18s var(--bm-ease)}
.why-link:hover{text-decoration-color:var(--bm-emerald)}
.why-link svg{transition:transform .18s var(--bm-ease)}
.why-link:hover svg{transform:translateX(3px)}

/* For makers */
.why-makers{background:#fff;padding:var(--why-pad) 0}
.why-makers-grid{display:grid;gap:32px}
.why-makers-t{font-weight:800;color:var(--bm-indigo);font-size:clamp(36px,4.6vw,68px);line-height:1.02;letter-spacing:-.04em;margin:0;max-width:14ch}
.why-makers-body{font-size:clamp(19px,1.6vw,23px);line-height:1.55;color:var(--bm-ink);margin:0;max-width:36ch;letter-spacing:-.008em}
.why-makers .swipe{--sd:.4s}
@media (min-width:900px){.why-makers-grid{grid-template-columns:7fr 5fr;gap:clamp(48px,6vw,96px);align-items:end}}

/* Close: the end card. The only fade on the page is this exit. */
.why-close{background:var(--bm-indigo);color:#fff;padding:clamp(80px,9vw,140px) 0 clamp(72px,8vw,120px)}
.why-close-badge{display:block;margin-bottom:clamp(32px,4vw,48px)}
.why-close-t{font-weight:800;font-size:clamp(52px,10.8vw,164px);line-height:.92;letter-spacing:-.05em;margin:0;color:#fff;}
.why-close .swipe{--sd:.5s}
.why-close-cta{display:flex;flex-wrap:wrap;align-items:center;gap:16px 28px;margin-top:clamp(48px,6vw,72px)}
.why-close-tour{font-weight:500;font-size:16px;color:rgba(255,255,255,.72);text-decoration:underline;text-decoration-color:rgba(255,255,255,.3);text-underline-offset:6px;text-decoration-thickness:1.5px}
.why-close-tour:hover{color:#fff;text-decoration-color:var(--bm-lime)}
.why-close .wrap>*{opacity:0}
.why-close.is-on .wrap>*{animation:why-exit 1.1s var(--bm-ease) forwards}
.why-close.is-on .why-close-t{animation-delay:.15s}
.why-close.is-on .why-close-cta{animation-delay:.8s}

@media (prefers-reduced-motion:reduce){
  .why .why-w,.why .why-open-line,.why .why-ruler,.why .why-ch-copy>*,.why .why-costs li,.why .why-turn-last,.why .why-belief-lines li,.why .why-promise li,.why .why-close .wrap>*{opacity:1!important;animation:none!important;clip-path:none!important;transform:none!important}
  .why .why-drift{transform:none!important;animation:none!important}
  .why .swipe::after{transform:none!important;transition:none!important}
}
`;
